// #region Imports
import WorkOutlineIcon from "@mui/icons-material/WorkOutlineOutlined";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { useMemo, useState } from "react";
import { useGetStaffingDestinationsQuery } from "work/staffing/_api/work-staffing-api";
import StaffingCandidateRail from "work/staffing/_views/staffing-candidate-rail";
import StaffingDestinationPanel from "work/staffing/_views/staffing-destination-panel";
import StaffingDestinationsTable from "work/staffing/_views/staffing-destinations-table";
import { staffingPickerLayout, useViewportWidth } from "work/staffing/_views/staffing-picker-stage";
// #endregion

// #region Styled Components
const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
}));
// #endregion

/** The picker's own name, and the header action's. */
export const ADD_TO_WORK = "Add to Work";

/** How many rows the availability question asks for. One is enough to answer "is there any?". */
export const AVAILABILITY_PAGE_SIZE = 1;

export type AddToWorkButtonProps = {
  /** The user whose profile is being looked at. */
  candidateUserId: Guid;
  /** The organization that user would be seated as. */
  candidateLegalEntityId: Guid;
  /** What that person is called, for the rail the profile shrinks to. */
  candidateName?: string;
  /** Where they work, for the same rail. */
  candidateCompanyName?: string;
  /** Their photograph, for the same rail. */
  candidateAvatarUrl?: string;
};

/**
 * Puts the person whose profile you are reading onto one of the pieces of work you run.
 *
 * The work it offers, which kind each one is, and whether the person is already on it come from
 * `work/staffing-destinations` -- never from a broader list narrowed here. A viewer who may staff
 * nothing gets an empty answer, and this renders nothing at all rather than a disabled control,
 * because an action nobody can take should not take up the header.
 *
 * **The picker takes a column of the profile's dialog, and never covers the profile.** It is not a
 * second dialog on the stack, not a dialog nested in one, and not a sheet floated over the right of
 * this one: the first two take the profile off the screen to show a table, and the third leaves it
 * on the screen and hides it, which comes to the same thing. The dialog divides instead, and how it
 * divides is what the window's width decides -- both panes, a context rail beside the table, or the
 * table with the profile pushed off and a way back on its title bar. The widths and the reasoning
 * are in `staffing-picker-stage`.
 *
 * The table is built when the picker opens and taken down when it closes, so a reader who comes
 * back to the picker gets a fresh first page rather than the search they left behind, and nothing
 * of the picker is left focusable behind a picker that is not out. Expanding the profile over it
 * does not take it down: that is the reader checking something about the person mid-search, and
 * the search is theirs to come back to.
 */
export default function AddToWorkButton(props: Readonly<AddToWorkButtonProps>) {
  const candidateUserId = props.candidateUserId;
  const candidateLegalEntityId = props.candidateLegalEntityId;
  const [open, setOpen] = useState(false);
  const [profileExpanded, setProfileExpanded] = useState(false);
  const viewportWidth = useViewportWidth();
  // Held rather than recomputed, because the panel asks the dialog for room whenever this changes
  // and the dialog answers by re-rendering everything drawn on it -- this one included. A fresh
  // object every render would make that a request the dialog can never be finished answering.
  const layout = useMemo(
    () => staffingPickerLayout(viewportWidth, profileExpanded),
    [profileExpanded, viewportWidth],
  );

  const candidate = {
    userId: candidateUserId.value,
    legalEntityId: candidateLegalEntityId.value,
  };

  // Whether the action belongs in the header at all. Deliberately a separate, unsearched question:
  // asking the same query the picker uses would make a search that matched nothing look like a
  // viewer who may staff nothing, and take the button away mid-search.
  const availability = useGetStaffingDestinationsQuery({
    candidate,
    pageSize: AVAILABILITY_PAGE_SIZE,
  });

  if (!availability.data || availability.data.totalCount === 0) return null;

  const closePicker = () => {
    setOpen(false);
    // A picker opened again starts from the profile, whatever the reader left expanded last time.
    setProfileExpanded(false);
  };

  return (
    <>
      <StyledButton
        variant="outlined"
        startIcon={<WorkOutlineIcon />}
        aria-expanded={open}
        // Also the way back to a picker the reader pushed aside to read the profile: the action
        // they came in by is the one control that is certainly on the screen at that point, and
        // "Add to Work" is still what they are asking for.
        onClick={() => {
          setOpen(true);
          setProfileExpanded(false);
        }}
      >
        {ADD_TO_WORK}
      </StyledButton>
      <StaffingDestinationPanel
        open={open}
        title={ADD_TO_WORK}
        layout={layout}
        rail={
          <StaffingCandidateRail
            name={props.candidateName ?? ""}
            companyName={props.candidateCompanyName}
            avatarUrl={props.candidateAvatarUrl}
            onExpand={() => setProfileExpanded(true)}
          />
        }
        onClose={closePicker}
        onShowPicker={() => setProfileExpanded(false)}
      >
        {open && (
          <StaffingDestinationsTable
            candidate={candidate}
            // Chosen and acted on: the picker has done its job, so it goes away and the reader is
            // left on the profile they started from, with the outcome snackbar over it.
            onChosen={closePicker}
          />
        )}
      </StaffingDestinationPanel>
    </>
  );
}
